import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import {
  TextField,
  Button,
  Container,
  Grid,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/system";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NumbersIcon from "@mui/icons-material/Numbers";
import PersonIcon from "@mui/icons-material/Person";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { createSecureLink } from "../services/firebaseLinkService";

import logo from "./LogoG.jpg";
import { useNavigate } from "react-router-dom";



function SendCCForm() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const [email, setEmail] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isLoading, setIsLoading] = useState(false);
  const [agentName, setAgentName] = useState("");

  const formatAmountInput = (value) => {
  const clean = String(value ?? "").replace(/[^\d.]/g, "");

  const parts = clean.split(".");
  const integerPart = parts[0] || "";
  const decimalPart = parts[1] ?? "";

  const formattedInteger = integerPart
    ? Number(integerPart).toLocaleString("en-US")
    : "";

  if (parts.length > 1) {
    return `${formattedInteger}.${decimalPart.slice(0, 2)}`;
  }

  return formattedInteger;
};

const handleAmountChange = (e) => {
  const rawValue = e.target.value;
  const formattedValue = formatAmountInput(rawValue);
  setAmount(formattedValue);
};

const handleSubmit = async (e) => {
  e.preventDefault();
  setIsLoading(true);

  if (!agentName.trim()) {
    setSnackbarMessage("Agent name is required");
    setSnackbarSeverity("error");
    setOpenSnackbar(true);
    setIsLoading(false);
    return;
  }

  try {
    const cleanAmount = String(amount).replace(/[^0-9.]/g, "");

    const formattedAmount = Number(cleanAmount || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      const secureLink = await createSecureLink({
        amount: formattedAmount,
        invoiceNumber,
        email,
        agentName,
        createdBy: "admin",
      });

      const templateParams = {
        to_email: email,
        amount: formattedAmount,
        link: secureLink.fullUrl,
        expiresAt: new Date(secureLink.expiresAt).toLocaleString(),
      };

      await emailjs.send(
        "service_f9cgj1h",
        "template_q69xfjr",
        templateParams,
        "AayvUU2KmVcoVafZF"
      );

      setSnackbarMessage("Secure link created and email sent successfully!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);

      setAmount("");
      setEmail("");
      setInvoiceNumber("");
    } catch (error) {
      console.error("Error creating secure link or sending email:", error);
      setSnackbarMessage("Error creating secure link or sending email.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  const StyledButton = styled(Button)({
    margin: "20px",
    fontWeight: "bold",
    backgroundColor: "#000",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#ff0000",
    },
  });

  return (
    <>
      <Button onClick={() => navigate("/")}>
        <ArrowBackIcon /> Main menu
      </Button>
      <Container component="main" maxWidth="sm">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            component="h1"
            variant="h5"
            style={{
              fontFamily: "Lato, sans-serif",
              textTransform: "uppercase",
              marginBottom: "5px",
            }}
          >
            Credit Card Authorization
          </Typography>
          <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
        </Box>
        

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="amount"
                label="Amount"
                name="amount"
                autoFocus
                value={amount}
                onChange={handleAmountChange}
                InputProps={{
                  disableUnderline: true,
                  startAdornment: (
                    <AttachMoneyIcon style={{ fontSize: 16 }}></AttachMoneyIcon>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="invoice"
                label="Invoice Number"
                name="invoice"
                autoFocus
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                InputProps={{
                  disableUnderline: true,
                  startAdornment: (
                    <NumbersIcon style={{ fontSize: 16 }}></NumbersIcon>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="agent"
                label="Rep. Name"
                name="agent"
                autoFocus
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
                InputProps={{
                  disableUnderline: true,
                  startAdornment: (
                    <PersonIcon style={{ fontSize: 16 }}></PersonIcon>
                  ),
                }}
              />
            </Grid>
          </Grid>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="email"
            label="Customer's email"
            name="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <StyledButton
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            style={{ margin: "20px 0" }}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} style={{ marginRight: 8, color: 'white' }} />
                Creating Secure Link...
              </>
            ) : (
              'Create Secure Link'
            )}
          </StyledButton>
        </form>
        <Snackbar
          open={openSnackbar}
          autoHideDuration={4000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert 
            onClose={() => setOpenSnackbar(false)} 
            severity={snackbarSeverity}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
}

export default SendCCForm;
